/**
 * Beat 14's hill — the hedgerow's far field, with a knoll in it and the road
 * lowered into a cutting beyond the far hedge (`docs/beat-14-plan.md`).
 *
 * **Why the room needed changing at all.** Spec §7.14 is *"a hill, dawn gold,
 * the truck's taillights on the road below"*, and beat 7 built the road on an
 * EMBANKMENT at y +1.6 — 3.4 m above the far field — so that it would show over
 * the far hedge from the yard. From anywhere she can stand, that road is above
 * her. So this room raises her (the knoll) and lowers the road (a cutting), and
 * leaves everything south of z −62 exactly as beats 7 and 10 have it.
 *
 * **One statement of the ground, again.** `hillGroundY` is `hedgerowGroundY`
 * plus the knoll, and the knoll's slabs, its grass and her spawn all read it —
 * the room's rule (docs/hedgerow-layout.md) carried over rather than copied.
 * Like the hedgerow's, it is C0 and nowhere C1: a knoll has breaks of slope.
 *
 * The compass is the hedgerow's: −Z is north (the field, the road, away), +Z is
 * the farm, and +X is WEST, which is where the truck goes.
 */
import * as THREE from 'three';
import { mulberry32 } from '@/audio/prng';
import { getAsset } from '@/models/registry';
import { surface } from '@/models/materials';
import { instanceGroup, type Placement } from '@/world/instancing';
import { solidMeshes, type HallSolid } from '@/world/solids';
import { HEDGEROW_EARTH, HEDGEROW_ROAD, hedgerowGroundY, slope } from '@/world/hedgerow';
import { getZone } from '@/render/palette';
import type { SpawnPoint } from '@/physics/character';

/**
 * The knoll's cross-section, south to north, in (z, y). Its two ends lie ON
 * the hedgerow's field line, so the join is continuous by construction and the
 * field slab underneath can stay (the knoll's slabs sit on top of it).
 *
 * **The crest is 4.3 m above the field**, and it has to be: her eye must clear
 * the far hedge's top (y +0.4) by enough to see down into the cutting, and the
 * first build — a 2.7 m knoll — photographed a wall of hedge at eye level with
 * the truck's roof showing over it. The south face is the way up (9–43 %); the
 * north face drops to the hedge, and `HILL_TOE_Z` stops her above its steepest
 * part.
 */
export const HILL_PROFILE: readonly { readonly z: number; readonly y: number }[] = [
  { z: -62, y: hedgerowGroundY(0, -62) },
  { z: -68, y: -0.9 },
  { z: -73, y: 0.6 },
  { z: -76, y: 1.9 },
  { z: -78, y: 2.45 },
  { z: -79.5, y: 2.5 },
  { z: -81, y: 2.15 },
  { z: -83, y: 1.0 },
  { z: -86, y: hedgerowGroundY(0, -86) },
];

/**
 * How far down the north face she can go. Past this the toe falls at 93 % to
 * the hedge's foot — over her 78 % climb limit, so a pig who went down could
 * not come back up — and the frames only ever needed the crest. Invisible:
 * the grass and the hedge beyond it are what she sees stop her.
 */
export const HILL_TOE_Z = -83;

const KNOLL_SOUTH = -62;
/** Where the knoll starts to rise, for the room's grass. */
export const HILL_KNOLL_SOUTH = KNOLL_SOUTH;
const KNOLL_NORTH = -86;

/** THE ground for this room: the hedgerow's, plus the knoll. */
export function hillGroundY(x: number, z: number): number {
  if (z > KNOLL_SOUTH || z < KNOLL_NORTH) return hedgerowGroundY(x, z);
  for (let index = 1; index < HILL_PROFILE.length; index += 1) {
    const a = HILL_PROFILE[index - 1];
    const b = HILL_PROFILE[index];
    if (a === undefined || b === undefined) break;
    if (z <= a.z && z >= b.z) return a.y + (b.y - a.y) * ((a.z - z) / (a.z - b.z));
  }
  return hedgerowGroundY(x, z);
}

/**
 * The knoll's slabs span far wider than her bounds (x −17…14) on purpose: every
 * camera in this beat looks along or across the ridge, and an end face at the
 * field's own x −28/+22 photographs as a cliff.
 */
const RIDGE_MIN_X = -170;
const RIDGE_MAX_X = 70;

/** The walkable knoll: colliders and meshes from this one list. */
export function hillSolids(): HallSolid[] {
  const solids: HallSolid[] = [];
  for (let index = 1; index < HILL_PROFILE.length; index += 1) {
    const a = HILL_PROFILE[index - 1];
    const b = HILL_PROFILE[index];
    if (a === undefined || b === undefined) continue;
    const [high, low] = a.y >= b.y ? [a, b] : [b, a];
    solids.push(slope(`knoll-${index}`, RIDGE_MIN_X, RIDGE_MAX_X, high, low, HEDGEROW_EARTH));
  }
  solids.push({
    name: 'knoll-toe-stop',
    half: { x: 20, y: 1.5, z: 0.25 },
    pos: { x: -1.5, y: hillGroundY(0, HILL_TOE_Z) + 1.2, z: HILL_TOE_Z - 0.25 },
    colour: HEDGEROW_EARTH,
    kind: 'organic',
    visible: false,
  });
  return solids;
}

/** Where the road runs, lowered: its top face, and the lane the truck drives. */
export const HILL_ROAD = { z: -100, top: -3.0, halfWidth: 3.5, laneZ: -99.1 } as const;

/**
 * Beyond her bounds, visual only: the field carried out past its old edges
 * both ways, the cutting and its road, and the country on the far side.
 * Nothing here is ever reachable — the far hedge's collider and the stock
 * fences stop her — so none of it is a collider.
 */
export function hillScenery(): HallSolid[] {
  const fieldHigh = { z: -20.4, y: hedgerowGroundY(0, -20.4) };
  const fieldLow = { z: -62, y: hedgerowGroundY(0, -62) };
  const hedgeFoot = { z: KNOLL_NORTH, y: hedgerowGroundY(0, KNOLL_NORTH) };
  const roadEdge = { z: HILL_ROAD.z + HILL_ROAD.halfWidth, y: HILL_ROAD.top - 0.05 };
  const farEdge = { z: HILL_ROAD.z - HILL_ROAD.halfWidth, y: HILL_ROAD.top - 0.05 };
  // Low, so the far country falls below her eyeline and the horizon is the
  // sky's: at +1.4 it stood up as a dark wall across the top of every frame.
  const farTop = { z: -114, y: -1.2 };
  const road = HILL_ROAD;
  return [
    // The field, out past where beat 7 stopped drawing it.
    slope('hill-field-west', 22, RIDGE_MAX_X, fieldHigh, fieldLow, HEDGEROW_EARTH),
    slope('hill-field-east', RIDGE_MIN_X, -28, fieldHigh, fieldLow, HEDGEROW_EARTH),
    // The cutting: down from the far hedge's foot to the road, and up the far side.
    slope('hill-cut-near', RIDGE_MIN_X, RIDGE_MAX_X, hedgeFoot, roadEdge, HEDGEROW_EARTH),
    {
      name: 'hill-road',
      half: { x: (RIDGE_MAX_X - RIDGE_MIN_X) / 2, y: 0.3, z: road.halfWidth },
      pos: { x: (RIDGE_MAX_X + RIDGE_MIN_X) / 2, y: road.top - 0.3, z: road.z },
      colour: HEDGEROW_ROAD,
      kind: 'mineral',
    },
    slope('hill-cut-far', RIDGE_MIN_X, RIDGE_MAX_X, farTop, farEdge, HEDGEROW_EARTH),
    {
      name: 'hill-far-country',
      half: { x: (RIDGE_MAX_X - RIDGE_MIN_X) / 2, y: 0.6, z: 150 },
      pos: { x: (RIDGE_MAX_X + RIDGE_MIN_X) / 2, y: farTop.y - 0.6, z: farTop.z - 150 },
      colour: HEDGEROW_EARTH,
      kind: 'organic',
    },
  ];
}

/**
 * She opens on the crest facing the road and the way the truck goes:
 * north-EAST, toward the dawn. East is −X (docs/hedgerow-layout.md), and the
 * first build sent the truck west, which pointed every shot of stage 1 away
 * from the only gold in the sky — the frame came back blue-grey. Not straight
 * into the key either: the toon ramp has no rim term, and a lens looking up a
 * 17° sun photographs the unlit side of everything (docs/traps.md). About
 * 30° off it.
 */
export const HILL_SPAWN: SpawnPoint = {
  x: -4,
  y: hillGroundY(-4, -79),
  z: -79,
  facing: Math.atan2(-0.6, -1),
};

/**
 * Holly's bell, in the wet grass on the north face — downhill, a few metres
 * from where she stands, between her and the road it went down. It is not on
 * her line of sight at the open (she faces the road), so she finds it by
 * looking down, which is the whole of stage 2.
 */
export const HILL_BELL_AT = { x: -2.2, z: -81.3 } as const;
export const HILL_BELL_Y = hillGroundY(HILL_BELL_AT.x, HILL_BELL_AT.z);

/**
 * The truck's lane, west to east (+X to −X): it comes past below her and away
 * into the sunrise. `goneX` is behind the roadside trees (`ROADSIDE_TREES`),
 * where it passes out of sight — the stage-1 end the room reports.
 */
export const HILL_TRUCK = { fromX: 16, goneX: -96, cruise: 9, pullAway: 2, rampSeconds: 8 } as const;

/** Where the truck is `t` seconds after it starts: a pull-away, then a cruise, eastward. */
export function hillTruckX(t: number): number {
  const ramp = HILL_TRUCK.rampSeconds;
  const accel = (HILL_TRUCK.cruise - HILL_TRUCK.pullAway) / ramp;
  if (t <= 0) return HILL_TRUCK.fromX;
  if (t <= ramp) return HILL_TRUCK.fromX - (HILL_TRUCK.pullAway * t + 0.5 * accel * t * t);
  const atRamp = HILL_TRUCK.pullAway * ramp + 0.5 * accel * ramp * ramp;
  return HILL_TRUCK.fromX - atRamp - HILL_TRUCK.cruise * (t - ramp);
}

/** True once the truck has passed behind the trees. */
export function hillTruckGone(x: number): boolean {
  return x <= HILL_TRUCK.goneX;
}

/** A stand of trees on the cutting's near bank, which the road goes behind as it leaves. */
const ROADSIDE_TREES: readonly Placement[] = [
  { x: -84, z: -95.5, rotY: 0.4 },
  { x: -91, z: -96.2, rotY: 2.1 },
  { x: -99, z: -95.2, rotY: 4.0 },
  { x: -107, z: -96.0, rotY: 1.2 },
];

/** The cutting's near bank, which `hill-cut-near` lays from the hedge's foot down to the road's edge. */
function cutNearBankY(z: number): number {
  const top = { z: KNOLL_NORTH, y: hedgerowGroundY(0, KNOLL_NORTH) };
  const bottom = { z: HILL_ROAD.z + HILL_ROAD.halfWidth, y: HILL_ROAD.top - 0.05 };
  return top.y + (bottom.y - top.y) * ((top.z - z) / (top.z - bottom.z));
}

/**
 * Tufts on the knoll, where the lenses are: dense on the crest and the north
 * face (the opening, the grass, the mouth and the turn are all shot there),
 * thin on the long south face, which only the turn sees and only far off. The
 * first build spread 2 600 evenly and put the room 70 000 triangles over the
 * hedgerow's ceiling for tufts no frame showed.
 */
export function hillGrassPlacements(): Placement[] {
  const random = mulberry32(1414);
  const placements: Placement[] = [];
  const bands = [
    { minX: -14, maxX: 8, north: HILL_TOE_Z + 0.3, south: -76, count: 560 },
    { minX: -17, maxX: 14, north: -76, south: KNOLL_SOUTH, count: 180 },
  ];
  for (const band of bands) {
    for (let index = 0; index < band.count; index += 1) {
      const x = band.minX + random() * (band.maxX - band.minX);
      const z = band.south - random() * (band.south - band.north);
      placements.push({ x, y: hillGroundY(x, z), z, rotY: random() * Math.PI * 2 });
    }
  }
  return placements;
}

/**
 * The farm, seen from the hill: a line of long roofs and one silo, far off to
 * the south. Silhouettes, not buildings — at 90–130 m in the dawn haze a
 * pitched roof is a shape and nothing else. Two draw calls.
 */
function farmSkyline(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'hill-farm-skyline';
  const zone = getZone('yard');
  const wall = surface('mineral', zone?.surfaces[2] ?? 0x7d8590);
  const roof = surface('metal', zone?.surfaces[1] ?? 0x5c636d);
  // Close and tall enough to stand clear of the haze: at 36–58 m back and
  // 4 m high the first build's roofs dissolved into the horizon entirely.
  const sheds = [
    { x: -10, z: 22, length: 46, width: 13, height: 5.5 },
    { x: 24, z: 30, length: 38, width: 15, height: 6 },
    { x: -40, z: 34, length: 30, width: 12, height: 5 },
  ];
  const walls: THREE.BufferGeometry[] = [];
  const roofs: THREE.BufferGeometry[] = [];
  for (const shed of sheds) {
    const body = new THREE.BoxGeometry(shed.length, shed.height, shed.width);
    body.translate(shed.x, shed.height / 2 + hedgerowGroundY(shed.x, shed.z), shed.z);
    walls.push(body);
    // A pitched roof: a triangular prism along X.
    const rise = shed.width * 0.28;
    const shape = new THREE.Shape([
      new THREE.Vector2(-shed.width / 2 - 0.4, 0),
      new THREE.Vector2(shed.width / 2 + 0.4, 0),
      new THREE.Vector2(0, rise),
    ]);
    const prism = new THREE.ExtrudeGeometry(shape, { depth: shed.length + 0.8, bevelEnabled: false });
    prism.rotateY(Math.PI / 2);
    prism.translate(shed.x - shed.length / 2 - 0.4, shed.height + hedgerowGroundY(shed.x, shed.z), shed.z);
    roofs.push(prism);
  }
  const silo = new THREE.CylinderGeometry(3.4, 3.4, 18, 16);
  silo.translate(8, 9, 26);
  walls.push(silo);
  const cap = new THREE.ConeGeometry(3.6, 2.4, 16);
  cap.translate(8, 19.1, 26);
  roofs.push(cap);
  for (const [name, geometries, material] of [['hill-farm-walls', walls, wall], ['hill-farm-roofs', roofs, roof]] as const) {
    const merged = mergeGeometries(geometries);
    const mesh = new THREE.Mesh(merged, material);
    mesh.name = name;
    group.add(mesh);
  }
  return group;
}

function mergeGeometries(geometries: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const flat = geometries.map((geometry) => (geometry.index === null ? geometry : geometry.toNonIndexed()));
  let count = 0;
  for (const geometry of flat) count += geometry.getAttribute('position').count;
  const positions = new Float32Array(count * 3);
  const normals = new Float32Array(count * 3);
  let offset = 0;
  for (const geometry of flat) {
    geometry.computeVertexNormals();
    positions.set(geometry.getAttribute('position').array, offset * 3);
    normals.set(geometry.getAttribute('normal').array, offset * 3);
    offset += geometry.getAttribute('position').count;
    geometry.dispose();
  }
  const merged = new THREE.BufferGeometry();
  merged.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  merged.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  return merged;
}

function assetOrThrow(id: string): THREE.Object3D {
  const asset = getAsset(id);
  if (asset === undefined) throw new Error(`hill: asset "${id}" is not registered`);
  return asset.create();
}

/** Everything the hill adds to the hedgerow that is not the knoll's own ground. */
export function createHillDressing(options: { dressing: boolean }): THREE.Group {
  const group = new THREE.Group();
  group.name = 'hill-dressing';
  group.add(solidMeshes(hillSolids(), 'hill-knoll'));
  group.add(solidMeshes(hillScenery(), 'hill-scenery'));
  if (!options.dressing) return group;
  group.add(instanceGroup(assetOrThrow('grass-tuft'), hillGrassPlacements(), 'hill-grass'));
  group.add(instanceGroup(
    assetOrThrow('field-tree'),
    ROADSIDE_TREES.map((at) => ({ ...at, y: cutNearBankY(at.z) })),
    'hill-roadside-trees',
  ));
  group.add(farmSkyline());
  return group;
}
